// The icons of the page: lines on a 24 × 24 grid, drawn in the text's colour (`currentColor`), as large as
// the text unless told. A button that is only an icon says what it does in `title` and `aria-label`.
import type { ComponentChildren, TargetedMouseEvent } from "preact";

const PATHS = {
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </>
  ),
  micOff: (
    <>
      <path d="M9 9V6a3 3 0 0 1 5.8-1" />
      <path d="M15 10v1a3 3 0 0 1-4.6 2.5" />
      <path d="M5 11a7 7 0 0 0 11.3 5.5" />
      <path d="M18.8 13A7 7 0 0 0 19 11" />
      <path d="M12 18v3" />
      <path d="M4 4l16 16" />
    </>
  ),
  camera: (
    <>
      <rect x="3" y="7" width="12" height="10" rx="2" />
      <path d="M15 11l6-3v8l-6-3z" />
    </>
  ),
  cameraOff: (
    <>
      <path d="M8 7h5a2 2 0 0 1 2 2v3" />
      <path d="M15 15v0a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2" />
      <path d="M17 12l4-2v6" />
      <path d="M4 4l16 16" />
    </>
  ),
  screen: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
      <path d="M9.5 10l2.5-2.5 2.5 2.5M12 7.5v5" />
    </>
  ),
  headphones: (
    <>
      <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
      <rect x="3" y="14" width="4" height="7" rx="1.5" />
      <rect x="17" y="14" width="4" height="7" rx="1.5" />
    </>
  ),
  headphonesOff: (
    <>
      <path d="M4 15v-3a8 8 0 0 1 12.5-6.6M19.4 9A8 8 0 0 1 20 12v3" />
      <rect x="3" y="14" width="4" height="7" rx="1.5" />
      <rect x="17" y="14" width="4" height="7" rx="1.5" />
      <path d="M3 3l18 18" />
    </>
  ),
  /** Who speaks: bars of a sound's level, each its own path, to move apart. */
  bars: (
    <>
      <path d="M7 10v4" />
      <path d="M12 6v12" />
      <path d="M17 9v6" />
    </>
  ),
  volume: (
    <>
      <path d="M4 10v4h3l5 4V6L7 10z" />
      <path d="M16 9a4 4 0 0 1 0 6" />
      <path d="M18.5 6.5a7.5 7.5 0 0 1 0 11" />
    </>
  ),
  hangup: (
    <path
      d="M2.5 14.4c5.3-5 13.7-5 19 0l-2.4 2.7a1 1 0 0 1-1.3.2l-2.7-1.7a1 1 0 0 1-.5-.9v-1.8a12 12 0 0 0-5.2 0v1.8a1 1 0 0 1-.5.9l-2.7 1.7a1 1 0 0 1-1.3-.2z"
      fill="currentColor"
      stroke="none"
    />
  ),
  link: (
    <>
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
      <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
    </>
  ),
  exit: (
    <>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="M15 8l4 4-4 4" />
      <path d="M19 12H9" />
    </>
  ),
  power: (
    <>
      <path d="M12 3v8" />
      <path d="M6.3 6.3a8 8 0 1 0 11.4 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M15.5 4.6a3.5 3.5 0 0 1 0 6.8" />
      <path d="M18 14.2a6.5 6.5 0 0 1 3.5 5.8" />
    </>
  ),
  userX: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M17 9l4 4" />
      <path d="M21 9l-4 4" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  settings: (
    <>
      <path d="M18.9 9.9L21.4 10.2V13.8L18.9 14.1L18.4 15.4L20 17.4L17.4 20L15.4 18.4L14.1 18.9L13.8 21.4H10.2L9.9 18.9L8.6 18.4L6.6 20L4 17.4L5.6 15.4L5.1 14.1L2.6 13.8V10.2L5.1 9.9L5.6 8.6L4 6.6L6.6 4L8.6 5.6L9.9 5.1L10.2 2.6H13.8L14.1 5.1L15.4 5.6L17.4 4L20 6.6L18.4 8.6z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  chat: <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />,
  send: (
    <>
      <path d="M21 3L3 10.5l7 2.5 2.5 7z" />
      <path d="M10 13l11-10" />
    </>
  ),
  hash: <path d="M5 9h14M5 15h14M10 4L8 20M16 4l-2 16" />,
  captions: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 10h4M14 10h3M7 14h7M17 14h0" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v11" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 20h14" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  pencil: (
    <>
      <path d="M15 5l4 4" />
      <path d="M4 20l1-5L16 4l4 4L9 19z" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M6 7l1 13h10l1-13" />
      <path d="M9 7V4h6v3" />
    </>
  ),
  shield: <path d="M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6z" />,
  back: (
    <>
      <path d="M19 12H5" />
      <path d="M11 6l-6 6 6 6" />
    </>
  ),
  crown: <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5z" />,
  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <path d="M9 9.5h.01M15 9.5h.01" />
    </>
  ),
  smilePlus: (
    <>
      <path d="M20.8 13.5A9 9 0 1 1 10.5 3.1" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <path d="M9 9.5h.01M15 9.5h.01" />
      <path d="M19 2v6M16 5h6" />
    </>
  ),
  reply: (
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 6 6v5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M21 16l-5-5-9 9" />
    </>
  ),
  arrowDown: (
    <>
      <path d="M12 5v14" />
      <path d="M6 13l6 6 6-6" />
    </>
  ),
  external: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </>
  ),
  chevronLeft: <path d="M15 6l-6 6 6 6" />,
  chevronRight: <path d="M9 6l6 6-6 6" />,
  chevronDown: <path d="M6 9l6 6 6-6" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </>
  ),
  speaker: (
    <>
      <rect x="3" y="4" width="18" height="11" rx="2" />
      <rect x="3" y="17.5" width="5" height="3" rx="1" />
      <rect x="9.5" y="17.5" width="5" height="3" rx="1" />
      <rect x="16" y="17.5" width="5" height="3" rx="1" />
    </>
  ),
  /** A board on its stand, a line drawn on it; and the board's tools. */
  board: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20l1.5-4M16 20l-1.5-4" />
      <path d="M7 12l3-3 2 2 4-4" />
    </>
  ),
  hand: <path d="M6 3l12 7-5.5 1.5L10 17z" />,
  eraser: (
    <>
      <path d="M4 15.5l8.5-8.5a2 2 0 0 1 2.8 0l3.2 3.2a2 2 0 0 1 0 2.8L12 19.5H8z" />
      <path d="M9 10.5l5 5" />
      <path d="M12 19.5h8" />
    </>
  ),
  line: <path d="M5 19L19 5" />,
  arrow: (
    <>
      <path d="M5 19L19 5" />
      <path d="M10 5h9v9" />
    </>
  ),
  rect: <rect x="4" y="6" width="16" height="12" rx="1" />,
  ellipse: <ellipse cx="12" cy="12" rx="9" ry="6.5" />,
  text: (
    <>
      <path d="M5 7V5h14v2" />
      <path d="M12 5v14" />
      <path d="M9 19h6" />
    </>
  ),
  laser: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </>
  ),
  undo: (
    <>
      <path d="M9 13L4 8l5-5" />
      <path d="M4 8h10a6 6 0 0 1 0 12h-3" />
    </>
  ),
  redo: (
    <>
      <path d="M15 13l5-5-5-5" />
      <path d="M20 8H10a6 6 0 0 0 0 12h3" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
};

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return (
    <svg
      class="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}

/** A button that is an icon: `title` says what it does (a tooltip, and what a screen reader reads). */
export function IconButton({
  icon,
  title,
  onClick,
  class: kind = "",
  pressed,
  children,
}: {
  icon: IconName;
  title: string;
  onClick: (e: TargetedMouseEvent<HTMLButtonElement>) => void;
  class?: string;
  pressed?: boolean;
  children?: ComponentChildren;
}) {
  return (
    <button class={`icon-button ${kind}`} title={title} aria-label={title} aria-pressed={pressed} onClick={onClick}>
      <Icon name={icon} />
      {children}
    </button>
  );
}
